import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { formatDate, formatTime } from "../lib/datetime.js";

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const MIN_SPAN = 10 * MIN;
const MAX_SPAN = 30 * 24 * HOUR;
/** A call row with no end time older than this is taken as lost (the voice service died), not as still connected. */
const STALE_OPEN_CALL = 3 * HOUR;
const LIMIT_KEY = "rc.channelLimit";

const WINDOWS: { label: string; span: number }[] = [
  { label: "1 hour", span: HOUR },
  { label: "6 hours", span: 6 * HOUR },
  { label: "24 hours", span: 24 * HOUR },
  { label: "7 days", span: 7 * 24 * HOUR },
];

interface Interval {
  start: number;
  end: number;
}

interface CallTimes {
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
}

/** When a call stopped being connected, in ms. An open call is live until `now`, unless it is old enough to be a lost row. */
export function callInterval(c: CallTimes, now: number): Interval {
  const start = Date.parse(c.started_at);
  if (c.ended_at) return { start, end: Math.max(start + MIN, Date.parse(c.ended_at)) };
  if (c.duration_seconds != null) return { start, end: start + Math.max(MIN, c.duration_seconds * 1000) };
  return { start, end: now - start > STALE_OPEN_CALL ? start + MIN : now };
}

/**
 * Peak simultaneous calls in each bucket of `bucketMin` minutes across [from, from + count * bucketMin).
 * Concurrency is counted per minute (a call is connected in every minute it overlaps), then a bucket takes its busiest minute,
 * so a short spike is never averaged away when zoomed out.
 */
export function peakPerBucket(intervals: Interval[], from: number, count: number, bucketMin: number): number[] {
  const minutes = count * bucketMin;
  const diff = new Array<number>(minutes + 1).fill(0);
  for (const iv of intervals) {
    const s = Math.max(0, Math.floor((iv.start - from) / MIN));
    const e = Math.min(minutes, Math.ceil((iv.end - from) / MIN));
    if (e <= s || s >= minutes) continue;
    diff[s] = (diff[s] ?? 0) + 1;
    diff[e] = (diff[e] ?? 0) - 1;
  }
  const out = new Array<number>(count).fill(0);
  let running = 0;
  for (let m = 0; m < minutes; m++) {
    running += diff[m] ?? 0;
    const b = Math.floor(m / bucketMin);
    if (running > (out[b] ?? 0)) out[b] = running;
  }
  return out;
}

/** Memory line: one point at the middle of each bucket, broken wherever a bucket has no sample (the service was down or not reporting yet). */
export function linePathWithGaps(values: (number | null)[], xOf: (i: number) => number, yOf: (v: number) => number): string {
  let d = "";
  let pen = false;
  values.forEach((v, i) => {
    if (v == null) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${xOf(i + 0.5).toFixed(1)},${yOf(v).toFixed(1)} `;
    pen = true;
  });
  return d.trim();
}

/** A round top for the memory axis: a multiple of 64 MB (a Fly machine's sizes are), at least the biggest value drawn. */
export function memoryAxisTop(maxValue: number): number {
  return Math.max(64, Math.ceil(maxValue / 64) * 64);
}

const W = 720;
const H = 240;
const PAD = { left: 36, right: 54, top: 12, bottom: 26 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function readLimit(): string {
  try {
    return window.localStorage.getItem(LIMIT_KEY) ?? "";
  } catch {
    return "";
  }
}

function tickLabel(t: number, span: number): string {
  return span > 24 * HOUR ? `${formatDate(t).slice(0, 5)} ${formatTime(t)}` : formatTime(t);
}

/** Simultaneous phone connections, per minute, over a time window you can drag and zoom. */
export function ConcurrencyChart() {
  const [span, setSpan] = useState(6 * HOUR);
  const [viewEnd, setViewEnd] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [loaded, setLoaded] = useState<{ from: number; to: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limitText, setLimitText] = useState(readLimit);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [showMemory, setShowMemory] = useState(true);
  const [memory, setMemory] = useState<{ used: (number | null)[]; total: number | null } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ x: number; end: number } | null>(null);

  const live = viewEnd >= now - MIN;
  const viewStart = viewEnd - span;

  // Keep "now" current so the live edge follows the clock.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (live) setViewEnd(now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  const load = useCallback(async (from: number, to: number) => {
    const { data, error: err } = await supabase
      .from("calls")
      .select("started_at, ended_at, duration_seconds")
      .lt("started_at", new Date(to).toISOString())
      .or(`ended_at.gte.${new Date(from).toISOString()},ended_at.is.null`)
      .limit(10000);
    if (err) {
      setError(err.message);
      return;
    }
    setError(null);
    const at = Date.now();
    setIntervals(((data ?? []) as CallTimes[]).map((c) => callInterval(c, at)));
    setLoaded({ from, to });
  }, []);

  // Fetch the visible window plus one window either side, and again when panning leaves it (or live data goes stale).
  useEffect(() => {
    const fresh = loaded && viewStart >= loaded.from && viewEnd <= loaded.to && !(live && loaded.to < now);
    if (fresh) return;
    void load(viewStart - span, Math.max(viewEnd, now) + span / 4);
  }, [viewStart, viewEnd, span, live, now, loaded, load]);

  const bucketMin = Math.max(1, Math.ceil(span / MIN / 360));
  const bucketCount = Math.ceil(span / MIN / bucketMin);
  const series = useMemo(() => peakPerBucket(intervals, viewStart, bucketCount, bucketMin), [intervals, viewStart, bucketCount, bucketMin]);

  // The voice machine's memory, the busiest sample in each bucket, over exactly the buckets drawn (same slices as the call counts).
  useEffect(() => {
    if (!showMemory) return;
    let stale = false;
    const id = window.setTimeout(async () => {
      const from = new Date(viewStart).toISOString();
      const to = new Date(viewStart + bucketCount * bucketMin * MIN).toISOString();
      const { data, error: err } = await supabase.rpc("rpc_admin_voice_memory", { p_from: from, p_to: to, p_buckets: bucketCount });
      if (stale) return;
      if (err) {
        setMemory(null);
        return;
      }
      const used: (number | null)[] = new Array(bucketCount).fill(null);
      let total: number | null = null;
      for (const r of (data ?? []) as { bucket: number; used_mb: number | string; total_mb: number | string | null }[]) {
        if (r.bucket >= 0 && r.bucket < bucketCount) used[r.bucket] = Number(r.used_mb);
        if (r.total_mb != null) total = Math.max(total ?? 0, Number(r.total_mb));
      }
      setMemory({ used, total });
    }, 300);
    return () => {
      stale = true;
      window.clearTimeout(id);
    };
  }, [showMemory, viewStart, bucketCount, bucketMin]);

  const limit = Number(limitText);
  const hasLimit = limitText.trim() !== "" && Number.isFinite(limit) && limit > 0;
  const peak = Math.max(0, ...series);
  const peakIdx = series.indexOf(peak);
  const yMax = Math.max(1, peak, hasLimit ? limit : 0);
  const yStep = yMax <= 6 ? 1 : Math.ceil(yMax / 5);
  const yTop = Math.ceil(yMax / yStep) * yStep;
  const yTicks: number[] = [];
  for (let v = 0; v <= yTop; v += yStep) yTicks.push(v);

  const memUsed = showMemory && memory ? memory.used : null;
  const memMax = memUsed ? Math.max(0, ...memUsed.map((v) => v ?? 0), memory?.total ?? 0) : 0;
  const memTop = memoryAxisTop(memMax);
  const memTicks = [0, 1, 2, 3, 4].map((k) => (memTop * k) / 4);
  const memPeak = memUsed ? Math.max(0, ...memUsed.map((v) => v ?? 0)) : 0;

  const xOf = (i: number) => PAD.left + (i / bucketCount) * PLOT_W;
  const yOf = (v: number) => PAD.top + PLOT_H - (v / yTop) * PLOT_H;
  const line: string[] = [];
  series.forEach((v, i) => {
    line.push(`${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`);
    line.push(`L${xOf(i + 1).toFixed(1)},${yOf(v).toFixed(1)}`);
  });
  const yOfMem = (v: number) => PAD.top + PLOT_H - (v / memTop) * PLOT_H;
  const area = `${line.join(" ")} L${xOf(bucketCount)},${yOf(0)} L${xOf(0)},${yOf(0)} Z`;

  const xTicks: number[] = [];
  const tickEvery = span <= 2 * HOUR ? 15 * MIN : span <= 8 * HOUR ? HOUR : span <= 48 * HOUR ? 6 * HOUR : 24 * HOUR;
  for (let t = Math.ceil(viewStart / tickEvery) * tickEvery; t <= viewEnd; t += tickEvery) xTicks.push(t);

  function clampEnd(end: number): number {
    return Math.min(end, Date.now());
  }
  function pan(by: number) {
    setViewEnd((e) => clampEnd(e + by));
  }
  function zoom(factor: number, anchorFrac = 1) {
    const next = Math.min(MAX_SPAN, Math.max(MIN_SPAN, span * factor));
    // Keep the instant under the cursor fixed.
    const anchor = viewStart + span * anchorFrac;
    setSpan(next);
    setViewEnd(clampEnd(anchor + next * (1 - anchorFrac)));
  }

  // Wheel needs a non-passive listener so the page doesn't scroll while zooming.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const rect = el!.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, ((e.clientX - rect.left) / rect.width) * W - PAD.left) / PLOT_W);
      zoomRef.current(e.deltaY > 0 ? 1.25 : 0.8, frac);
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  function plotFrac(e: ReactPointerEvent): number {
    const rect = svgRef.current!.getBoundingClientRect();
    return Math.min(1, Math.max(0, (((e.clientX - rect.left) / rect.width) * W - PAD.left) / PLOT_W));
  }
  function onPointerDown(e: ReactPointerEvent) {
    drag.current = { x: e.clientX, end: viewEnd };
    svgRef.current?.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: ReactPointerEvent) {
    if (drag.current) {
      const rect = svgRef.current!.getBoundingClientRect();
      const plotPx = ((e.clientX - drag.current.x) / rect.width) * W;
      setViewEnd(clampEnd(drag.current.end - (plotPx / PLOT_W) * span));
      return;
    }
    setHoverIdx(Math.min(bucketCount - 1, Math.floor(plotFrac(e) * bucketCount)));
  }
  function onPointerUp(e: ReactPointerEvent) {
    drag.current = null;
    svgRef.current?.releasePointerCapture(e.pointerId);
  }

  function setLimit(v: string) {
    setLimitText(v);
    try {
      window.localStorage.setItem(LIMIT_KEY, v);
    } catch {
      /* per-viewer convenience only */
    }
  }

  const hoverValue = hoverIdx != null ? series[hoverIdx] ?? 0 : null;
  const hoverMemory = hoverIdx != null && memUsed ? memUsed[hoverIdx] ?? null : null;
  const hoverTime = hoverIdx != null ? viewStart + hoverIdx * bucketMin * MIN : null;
  const btn: React.CSSProperties = { padding: "4px 10px", cursor: "pointer" };

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 className="rc-h2" style={{ margin: "0 0 4px" }}>Simultaneous phone connections</h2>
      <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--rc-text-muted)" }}>
        {peak > 0 && peakIdx >= 0 ? `Peak ${peak} at ${tickLabel(viewStart + peakIdx * bucketMin * MIN, span)}` : "No calls in this window"}
        {" · "}
        {hoverValue != null && hoverTime != null
          ? `${tickLabel(hoverTime, span)}: ${hoverValue} ${hoverValue === 1 ? "call" : "calls"}${hoverMemory != null ? `, ${Math.round(hoverMemory)} MB memory` : ""}`
          : "Hover for a minute's count"}
        {bucketMin > 1 ? ` (busiest minute of each ${bucketMin})` : ""}
      </p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8, fontSize: 13 }}>
        <button style={btn} onClick={() => pan(-span / 2)} aria-label="Earlier">◀</button>
        <button style={btn} onClick={() => pan(span / 2)} disabled={live} aria-label="Later">▶</button>
        <button style={btn} onClick={() => setViewEnd(Date.now())} disabled={live}>Now</button>
        {WINDOWS.map((w) => (
          <button
            key={w.label}
            style={{ ...btn, fontWeight: span === w.span ? 700 : 400 }}
            onClick={() => {
              setSpan(w.span);
              setViewEnd((e) => clampEnd(e));
            }}
          >
            {w.label}
          </button>
        ))}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" checked={showMemory} onChange={(e) => setShowMemory(e.target.checked)} />
          <span style={{ width: 14, height: 0, borderTop: "2px solid var(--rc-warning)", display: "inline-block" }} aria-hidden="true" />
          Fly memory
        </label>
        <label style={{ marginLeft: "auto" }}>
          Channel limit{" "}
          <input
            type="number"
            min={1}
            value={limitText}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="none"
            style={{ width: 64, padding: 4 }}
          />
        </label>
      </div>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Simultaneous phone connections over time"
        style={{ width: "100%", height: "auto", touchAction: "none", cursor: drag.current ? "grabbing" : "grab", userSelect: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => setHoverIdx(null)}
      >
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={yOf(v)} y2={yOf(v)} stroke="var(--rc-border-soft)" />
            <text x={PAD.left - 6} y={yOf(v) + 4} textAnchor="end" fontSize={11} fill="var(--rc-text-muted)">{v}</text>
          </g>
        ))}
        {xTicks.map((t) => {
          const x = PAD.left + ((t - viewStart) / span) * PLOT_W;
          return (
            <text key={t} x={x} y={H - 8} textAnchor="middle" fontSize={11} fill="var(--rc-text-muted)">{tickLabel(t, span)}</text>
          );
        })}
        <path d={area} fill="var(--rc-link)" fillOpacity={0.25} />
        <path d={line.join(" ")} fill="none" stroke="var(--rc-link)" strokeWidth={1.5} />
        {memUsed && (
          <g>
            {memTicks.map((v) => (
              <text key={v} x={W - PAD.right + 6} y={yOfMem(v) + 4} fontSize={11} fill="var(--rc-warning)">{Math.round(v)}{v === memTop ? " MB" : ""}</text>
            ))}
            {memory?.total != null && (
              <line x1={PAD.left} x2={W - PAD.right} y1={yOfMem(memory.total)} y2={yOfMem(memory.total)} stroke="var(--rc-warning)" strokeOpacity={0.5} strokeDasharray="2 4" />
            )}
            <path d={linePathWithGaps(memUsed, xOf, yOfMem)} fill="none" stroke="var(--rc-warning)" strokeWidth={2} />
          </g>
        )}
        {hasLimit && (
          <g>
            <line x1={PAD.left} x2={W - PAD.right} y1={yOf(limit)} y2={yOf(limit)} stroke="var(--rc-danger)" strokeWidth={1.5} strokeDasharray="6 4" />
            <text x={W - PAD.right} y={yOf(limit) - 4} textAnchor="end" fontSize={11} fill="var(--rc-danger)">limit {limit}</text>
          </g>
        )}
        {hoverIdx != null && (
          <line x1={xOf(hoverIdx + 0.5)} x2={xOf(hoverIdx + 0.5)} y1={PAD.top} y2={PAD.top + PLOT_H} stroke="var(--rc-text-muted)" strokeDasharray="2 3" />
        )}
      </svg>
      <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--rc-text-faint)" }}>Drag to move through time, scroll to zoom. All businesses combined.{memUsed ? ` Fly memory (right scale, MB): peak ${Math.round(memPeak)}${memory?.total != null ? ` of ${Math.round(memory.total)}` : ""} in this window; the dotted line is the machine's total.` : ""}</p>
    </div>
  );
}
